import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { STATUS } from "../../../../store/slices/OrganizationSlice";
import { fetchSupportDebugUpcomingBillsAsync } from "../../../../store/thunks/SupportDebugThunk";
import StatCard from "../../../common/StatCard";
import BillsTable from "../BillsTable";
import { PanelErrorCard, PanelLoadingCard } from "../PanelStates";
import { formatCurrency, formatDate } from "../utils";

interface UpcomingBillsPanelProps {
  userId: string;
}

const UpcomingBillsPanel = ({ userId }: UpcomingBillsPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, status, error } = useSelector(
    (state: RootState) => state.SupportDebugStore.bills
  );

  // Tool data is cleared (back to IDLE) whenever the selected user changes,
  // so an IDLE status always means "not fetched for this user yet".
  useEffect(() => {
    if (userId && status === STATUS.IDLE) {
      dispatch(fetchSupportDebugUpcomingBillsAsync({ userId }));
    }
  }, [userId, status, dispatch]);

  if (status === STATUS.LOADING || status === STATUS.IDLE) {
    return <PanelLoadingCard />;
  }

  if (status === STATUS.FAILED) {
    return <PanelErrorCard error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <p className="mt-6 text-sm font-medium text-textColor3">
        Next {data.days} days: {formatDate(data.windowStart)} &rarr; {formatDate(data.windowEnd)}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <StatCard
          title="Total amount"
          description="Sum of the predicted bill amounts inside the window"
          value={formatCurrency(data.totalAmount)}
          valueLabel="in window"
        />
        <StatCard
          title="Monthly-normalized total"
          description="Each bill's amount normalized to a monthly figure, then summed"
          value={formatCurrency(data.totalMonthlyAmount)}
          valueLabel="per month"
        />
        <StatCard
          title="Bill count"
          description="Recurring bills predicted to land inside the window"
          value={data.count}
          valueLabel={data.count === 1 ? "bill" : "bills"}
        />
      </div>

      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Upcoming bills</div>
        <BillsTable bills={data.bills} emptyMessage="No upcoming bills in this window" />
      </div>
    </>
  );
};

export default UpcomingBillsPanel;
